import React, { useEffect, useState } from 'react';
import { Car, Loader2, Pencil, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminJobTravel = ({ job, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/jobs/${job.id}/travel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.ok ? await res.json() : null);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job.id, job.location]);

  const saveOverride = async (staffEmail, value) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/jobs/${job.id}/travel/override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          staff_email: staffEmail,
          travel_money: value === '' || value === null ? null : parseFloat(value),
        }),
      });
      if (res.ok) setData(await res.json());
    } finally {
      setSaving(false);
      setEditing(null);
      setDraft('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Working out travel…
      </div>
    );
  }

  if (!data || data.staff.length === 0) return null;

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`admin-travel-${job.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <Car className="w-3.5 h-3.5 text-blue-600" /> Travel
        </p>
        <p className="text-xs font-semibold text-slate-800" data-testid={`admin-travel-total-${job.id}`}>
          Job total: £{Number(data.total_travel_money).toFixed(2)}
        </p>
      </div>

      <div className="space-y-1.5">
        {data.staff.map((row) => (
          <div key={row.staff_email} className="flex items-center justify-between gap-2 text-xs" data-testid={`admin-travel-row-${row.staff_email}`}>
            <div className="min-w-0">
              <span className="font-medium text-slate-800">{row.staff_name}</span>
              {row.status === 'ok' || row.status === 'manual_only' ? (
                <span className="text-slate-500">
                  {row.distance_miles !== null && row.distance_miles !== undefined && (
                    <> · {Number(row.distance_miles).toFixed(1)} mi each way</>
                  )}
                  {row.duration_text && <> · {row.duration_text}</>}
                </span>
              ) : (
                <span className="text-slate-500"> · {row.message}</span>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {editing === row.staff_email ? (
                <>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="h-7 w-20 text-xs"
                    data-testid={`travel-override-input-${row.staff_email}`}
                  />
                  <Button
                    size="sm" variant="ghost" className="h-7 px-2" disabled={saving}
                    onClick={() => saveOverride(row.staff_email, draft)}
                    data-testid={`travel-override-save-${row.staff_email}`}
                  >
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing(null)}>
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="font-semibold text-slate-900">
                    {row.travel_money === null || row.travel_money === undefined
                      ? '—'
                      : `£${Number(row.travel_money).toFixed(2)}`}
                  </span>
                  {row.is_manual && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-amber-300 text-amber-700">manual</Badge>
                  )}
                  <Button
                    size="sm" variant="ghost" className="h-7 px-2"
                    onClick={() => {
                      setEditing(row.staff_email);
                      setDraft(row.travel_money !== null && row.travel_money !== undefined ? String(row.travel_money) : '');
                    }}
                    data-testid={`travel-override-edit-${row.staff_email}`}
                  >
                    <Pencil className="w-3 h-3 text-slate-400" />
                  </Button>
                  {row.is_manual && (
                    <Button
                      size="sm" variant="ghost" className="h-7 px-1.5 text-[10px] text-slate-500"
                      onClick={() => saveOverride(row.staff_email, null)}
                      data-testid={`travel-override-clear-${row.staff_email}`}
                    >
                      clear
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminJobTravel;
